import 'photoswipe/style.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

import { ApolloProvider } from '@apollo/client';
import { GoogleTagManager } from '@next/third-parties/google';
import { SaleorAuthProvider } from '@saleor/auth-sdk/react';
import * as Sentry from '@sentry/nextjs';
import type { NextPage, NextPageContext } from 'next';
import type { AppContext, AppProps } from 'next/app';
import App from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { SnackbarProvider } from 'notistack';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import {
  extractPageUrlFromNextContext,
  parseUrlOrigin,
  removeTrailingSlash,
} from '@/utils/urlUtils';

import SkipToContentLink from '@/components/A11y/SkipToContentLink';
import ErrorBoundaryWrapper from '@/components/ErrorBoundary';
import { FaviconLinks } from '@/components/FaviconLinks/FaviconLinks';
import { NextProgressWrapper } from '@/components/NextProgressWrapper';
import { Processing } from '@/components/Processing/Processing';
import SEOHead from '@/components/SEOHead/SEOHead';
import { saleorAuthClient } from '@/graphql/client';
import type { ContentfulSeoConfiguration } from '@/graphql/contentful/api';
import { useApollo } from '@/hooks/useApollo';
import { useRouter } from '@/hooks/useRouter';
import { useSearchInsights } from '@/hooks/useSearchInsightsInit';
import { GlobalEScript } from '@/integrations/global-e/GlobalEScript';
import { OneTrustScript } from '@/integrations/onetrust/OnetrustScript';
import { OneTrustScriptReloader } from '@/integrations/onetrust/OneTrustScriptReloader';
import { ZipScript } from '@/integrations/zip/ZipScript';
import {
  Brand,
  LocaleConfiguration,
  toBrandEnum,
} from '@/lib/configuration/configuration';
import {
  API_URI,
  BRAND,
  CONFIGURATION_APP_API_URI,
  GLOBAL_E_MARKET_AVAILABLE,
  GLOBAL_E_MERCHANT_ID,
  GLOBAL_E_URL,
  isWholeSalerWidget,
  MY_ACCOUNT_APP_API_URI,
  ORDER_API_URI,
  POS_MANAGER_APP_API_URI,
  SSR_MODE,
  storageInitSetting,
  VERSION,
} from '@/lib/const';
import type { LocaleMessages } from '@/lib/intl';
import { loadMessages } from '@/lib/intl';
import { getRegionFromLocale } from '@/lib/regions';
import { StorageHelper } from '@/lib/storage';
import { AlertBannerProvider } from '@/providers/AlertBannerProvider/AlertBannerContext';
import { AlgoliaEventProvider } from '@/providers/AlgoliaEventProvider';
import { CommonModalProvider } from '@/providers/CommonModalProvider';
import { ConfigProvider } from '@/providers/ConfigProvider/ConfigProvider';
import { ErrorProvider } from '@/providers/ErrorProvider/ErrorProvider';
import { GlobalEProvider } from '@/providers/GlobalEProvider/GlobalEProviderContext';
import { GoogleAnalyticsProvider } from '@/providers/GoogleAnalyticsProvider/GoogleAnalyticsProvider';
import { LocaleContext } from '@/providers/LocaleProvider';
import { ProcessingProvider } from '@/providers/ProcessingProvider/ProcessingProviderContext';
import { RegionProvider } from '@/providers/RegionProvider';
import { GlobalStyle } from '@/theme';
import { theme } from '@/theme/getTheme';

import { sentryInitDefaults } from '../../sentry.config';

const NotifyMeModal = dynamic(() =>
  import('@/views/WatchView/components/NotifyMeModal').then(
    (mod) => mod.NotifyMeModal,
  ),
);
const WarrantyExtensionModal = dynamic(() =>
  import('@/components/Cms/Event/WarrantyExtensionModal').then(
    (mod) => mod.WarrantyExtensionModal,
  ),
);

if (!SSR_MODE) {
  window.version = VERSION;
}

Sentry.init(sentryInitDefaults);

StorageHelper.init(storageInitSetting);

export type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  currentLocale: string;
  currentUrl: string;
  fallbackMessages: LocaleMessages;
};

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID as string;

function MyApp({
  Component,
  currentLocale,
  currentUrl,
  fallbackMessages,
  pageProps,
}: AppPropsWithLayout) {
  const [updateUrl, setUpdateUrl] = useState<string>('');
  const { asPath } = useRouter();

  useSearchInsights();

  useEffect(() => {
    if (currentUrl !== asPath) {
      setUpdateUrl(
        `${removeTrailingSlash(
          process.env.NEXT_PUBLIC_URL as string,
        )}${asPath}`,
      );
    }
  }, [asPath, currentUrl, currentLocale]);

  const showCookiesModal =
    !currentUrl?.includes('nocookiemodal=true') &&
    !currentUrl?.includes('://localhost') &&
    !isWholeSalerWidget(currentUrl);

  const getLayout = Component.getLayout ?? ((page: ReactElement) => page);
  const apolloClient = useApollo(pageProps);

  const contentfulMessages: LocaleMessages = pageProps.localeMessages ?? {};

  const localeMessages: LocaleMessages = {
    ...fallbackMessages,
    ...contentfulMessages,
  };

  if (pageProps.localeConfiguration) {
    // init locale configuration in client
    LocaleConfiguration.getInstance().setStates(pageProps.localeConfiguration);
  }

  const { country } = getRegionFromLocale(currentLocale);

  return (
    <>
      <Head>
        {BRAND == Brand.Gallet && (
          <meta
            content='QsjGwo_Fs6OGyW3Gb_GJRXdE_t49Tvv6UmH1q-qtHg0'
            name='google-site-verification'
          />
        )}
        <link
          crossOrigin='anonymous'
          href={parseUrlOrigin(API_URI)}
          rel='preconnect'
        />
        <link
          crossOrigin='anonymous'
          href={parseUrlOrigin(ORDER_API_URI)}
          rel='preconnect'
        />
        <link
          crossOrigin='anonymous'
          href={parseUrlOrigin(CONFIGURATION_APP_API_URI)}
          rel='preconnect'
        />
        <link
          crossOrigin='anonymous'
          href={parseUrlOrigin(POS_MANAGER_APP_API_URI)}
          rel='preconnect'
        />
        <link
          crossOrigin='anonymous'
          href={parseUrlOrigin(MY_ACCOUNT_APP_API_URI)}
          rel='preconnect'
        />
        <link href={parseUrlOrigin(API_URI)} rel='dns-prefetch' />
        <link href={parseUrlOrigin(ORDER_API_URI)} rel='dns-prefetch' />
        <link
          href={parseUrlOrigin(CONFIGURATION_APP_API_URI)}
          rel='dns-prefetch'
        />
        <link
          href={parseUrlOrigin(POS_MANAGER_APP_API_URI)}
          rel='dns-prefetch'
        />
        <link
          href={parseUrlOrigin(MY_ACCOUNT_APP_API_URI)}
          rel='dns-prefetch'
        />
        {/* Global-e styles */}
        {currentLocale &&
          GLOBAL_E_MARKET_AVAILABLE?.includes(currentLocale) && (
            <link
              href={`//${GLOBAL_E_URL}/includes/css/${GLOBAL_E_MERCHANT_ID}?countryCode=${country.code}`}
              id='GEPIStyles'
              rel='stylesheet'
            />
          )}
      </Head>
      <FaviconLinks brand={toBrandEnum(BRAND)} />
      <LocaleContext.Provider value={currentLocale}>
        <SEOHead
          requestUrl={updateUrl || currentUrl}
          seoConfig={pageProps.seoConfig as ContentfulSeoConfiguration}
        />
        <GlobalEProvider>
          <SaleorAuthProvider client={saleorAuthClient}>
            <ApolloProvider client={apolloClient}>
              <RegionProvider messages={localeMessages}>
                <SkipToContentLink />
                <AlgoliaEventProvider>
                  <AlertBannerProvider>
                    <ThemeProvider theme={theme}>
                      <SnackbarProvider
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        maxSnack={3}
                      >
                        <ErrorProvider>
                          <ErrorBoundaryWrapper>
                            <ConfigProvider apolloClient={apolloClient}>
                              <ProcessingProvider>
                                <CommonModalProvider
                                  NotifyMeComponent={NotifyMeModal}
                                  WarrantyExtensionComponent={
                                    WarrantyExtensionModal
                                  }
                                >
                                  <GoogleAnalyticsProvider>
                                    <GlobalStyle />
                                    <NextProgressWrapper />
                                    <Processing>
                                      {getLayout(<Component {...pageProps} />)}
                                    </Processing>
                                    <div id='modal-root' />
                                    <div id='listing-modal-root' />
                                  </GoogleAnalyticsProvider>
                                </CommonModalProvider>
                              </ProcessingProvider>
                            </ConfigProvider>
                          </ErrorBoundaryWrapper>
                        </ErrorProvider>
                      </SnackbarProvider>
                    </ThemeProvider>
                  </AlertBannerProvider>
                </AlgoliaEventProvider>
              </RegionProvider>
            </ApolloProvider>
          </SaleorAuthProvider>

          {currentLocale &&
            GLOBAL_E_MARKET_AVAILABLE?.includes(currentLocale) && (
              <GlobalEScript />
            )}
        </GlobalEProvider>
        {GTM_ID && !isWholeSalerWidget(currentUrl) && (
          <GoogleTagManager gtmId={GTM_ID} />
        )}
        {showCookiesModal && <OneTrustScript />}
        {showCookiesModal && <OneTrustScriptReloader />}
        {country?.code === 'AU' && <ZipScript />}
      </LocaleContext.Provider>
    </>
  );
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);
  const ctx: NextPageContext = appContext.ctx;
  const currentLocale = ctx.query.lang as string;
  const currentUrl = extractPageUrlFromNextContext(
    ctx,
    process.env.NEXT_PUBLIC_URL as string,
  );

  // MyApp.getInitialProps will run at the same time as ssr, so the locale configuration is still fallback data here.
  const { locale } = getRegionFromLocale(currentLocale);
  const fallbackMessages = await loadMessages(locale.code);

  return {
    ...appProps,
    currentUrl,
    fallbackMessages,
    currentLocale,
  };
};

export default MyApp;
